/* ─── Device location ───

   Two ways in:
   - On arrival, silently, only when the site already has location permission
     (captureDeviceLocationIfGranted). getCurrentPosition answers without a
     prompt then; in any other state it would prompt, so it is not called.
   - On the business step right after identity, when the creator answers
     Yes or No (requestDeviceLocation, inside the tap). This is the only place
     the browser prompt shows; the upload keeps running underneath.

   The Permissions API tells us the state without prompting. Safari is uneven:
   with "Allow" set it reports `granted` reliably, but a short-lived grant can
   still read as `prompt`, which we treat as not granted.

   Google Analytics forbids location finer than one square mile, including any
   latitude/longitude, so the position is reduced to a 5-character geohash cell
   (about 5 x 5 km at the equator, still several square miles near the poles)
   before it reaches analytics. BigQuery decodes it with
   ST_GEOGPOINTFROMGEOHASH. Raw coordinates stay in this module. */

export type DeviceLocationOutcome = 'captured' | 'not_granted' | 'denied' | 'unsupported' | 'error';
export type DeviceLocationTrigger = 'arrival' | 'permission_change' | 'business_confirm' | 'complete_screen';

export interface DeviceLocationResult {
  trigger: DeviceLocationTrigger;
  outcome: DeviceLocationOutcome;
  permission: PermissionState | 'unknown';
  error_code?: number;
  accuracy_m?: number;
  /** On captured: the same ~5 km geohash cell as the device_geo user property
      (own name, so it can be registered as an event dimension alongside the
      user-scoped device_geo). */
  geo_cell?: string;
  ms: number;
}

const GEOHASH_PRECISION = 5;
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

const encodeGeohash = (lat: number, lon: number, precision: number): string => {
  let latMin = -90;
  let latMax = 90;
  let lonMin = -180;
  let lonMax = 180;
  let hash = '';
  let bit = 0;
  let ch = 0;
  let evenBit = true;
  while (hash.length < precision) {
    if (evenBit) {
      const mid = (lonMin + lonMax) / 2;
      if (lon >= mid) {
        ch = (ch << 1) | 1;
        lonMin = mid;
      } else {
        ch <<= 1;
        lonMax = mid;
      }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat >= mid) {
        ch = (ch << 1) | 1;
        latMin = mid;
      } else {
        ch <<= 1;
        latMax = mid;
      }
    }
    evenBit = !evenBit;
    if (++bit === 5) {
      hash += BASE32[ch];
      bit = 0;
      ch = 0;
    }
  }
  return hash;
};

let cell: { geohash: string; accuracyM: number } | null = null;
let started = false;

export const hasDeviceLocation = (): boolean => cell !== null;

const isSupported = (): boolean => typeof window !== 'undefined' && window.isSecureContext && 'geolocation' in navigator;

/** Permission state without prompting; 'unknown' where the browser cannot say. */
export const getLocationPermissionState = async (): Promise<PermissionState | 'unknown'> => {
  if (!isSupported() || !navigator.permissions?.query) return 'unknown';
  try {
    return (await navigator.permissions.query({ name: 'geolocation' })).state;
  } catch {
    return 'unknown';
  }
};

/** User properties for analytics; empty until a location has been read. */
/** The captured cell, for the per-event envelope; null until captured. */
export const getGeoCell = (): string | null => cell?.geohash ?? null;

export const getDeviceLocationProps = (): Record<string, string> =>
  cell ? { device_geo: cell.geohash, device_geo_acc_m: String(cell.accuracyM) } : {};

const readPosition = (trigger: DeviceLocationTrigger, startedAt: number): Promise<DeviceLocationResult> =>
  new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const accuracyM = Math.round(pos.coords.accuracy);
        cell = { geohash: encodeGeohash(pos.coords.latitude, pos.coords.longitude, GEOHASH_PRECISION), accuracyM };
        resolve({
          trigger,
          outcome: 'captured',
          permission: 'granted',
          accuracy_m: accuracyM,
          // On the event too, so the capture is recorded in one hit.
          geo_cell: cell.geohash,
          ms: Math.round(performance.now() - startedAt),
        });
      },
      (err) =>
        resolve({
          trigger,
          // 1 = PERMISSION_DENIED: refused at the prompt, or blocked for the
          // site or the whole browser (then it fails without a prompt).
          outcome: err.code === 1 ? 'denied' : 'error',
          permission: err.code === 1 ? 'denied' : 'granted',
          error_code: err.code,
          ms: Math.round(performance.now() - startedAt),
        }),
      // A recent cached fix is fine: the cell is kilometres wide.
      { enableHighAccuracy: false, maximumAge: 10 * 60_000, timeout: 10_000 },
    );
  });

/**
 * Reads the device location once per page load if, and only if, the site
 * already has location permission. `onResult` gets one report per page load;
 * if permission turns to granted later in the page's life, the location is
 * read then and reported again.
 */
export const captureDeviceLocationIfGranted = (onResult: (result: DeviceLocationResult) => void): void => {
  if (started || typeof window === 'undefined') return;
  started = true;
  const startedAt = performance.now();
  const done = (r: Omit<DeviceLocationResult, 'ms' | 'trigger'>) =>
    onResult({ ...r, trigger: 'arrival', ms: Math.round(performance.now() - startedAt) });

  if (!isSupported() || !navigator.permissions?.query) {
    // Without the Permissions API we cannot tell granted from prompt, and
    // calling getCurrentPosition could prompt.
    done({ outcome: 'unsupported', permission: 'unknown' });
    return;
  }
  navigator.permissions
    .query({ name: 'geolocation' })
    .then((status) => {
      if (status.state === 'granted') {
        readPosition('arrival', startedAt).then(onResult);
        return;
      }
      done({ outcome: 'not_granted', permission: status.state });
      status.onchange = () => {
        if (status.state !== 'granted' || cell) return;
        status.onchange = null;
        readPosition('permission_change', performance.now()).then(onResult);
      };
    })
    .catch(() => done({ outcome: 'unsupported', permission: 'unknown' }));
};

/**
 * Asks for the location: shows the browser prompt unless the site already has
 * an answer. Called from a tap.
 */
export const requestDeviceLocation = (trigger: DeviceLocationTrigger): Promise<DeviceLocationResult> => {
  const startedAt = performance.now();
  if (!isSupported()) {
    return Promise.resolve({ trigger, outcome: 'unsupported', permission: 'unknown', ms: 0 });
  }
  return readPosition(trigger, startedAt);
};
